import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { crmBasePath } from '../utils/crmEnv';
import { createActivity, createDealFromMatch, createLead } from '../utils/crmApi';
import { fetchMatchesForProperty, fetchPropHuntLeadsForProperty, refreshMatchesForProperty } from '../api/matchingApi';
import { useToast } from './Toast';

const formatRelativeTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const defaultFollowupAt = () => {
    const next = new Date();
    next.setDate(next.getDate() + 1);
    next.setHours(11, 0, 0, 0);
    return next.getTime();
};

function PropertyMatchesModal({ property, onClose, onMakePublic }) {
    const history = useHistory();
    const basePath = crmBasePath();
    const { showSuccess, showError } = useToast();
    const requestSeqRef = useRef(0);
    const [matchesData, setMatchesData] = useState(null);
    const [matches, setMatches] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sort, setSort] = useState('best');
    const [activeSource, setActiveSource] = useState('my_leads');

    const propertyId = property?.id || property?._id;

    useEffect(() => {
        const handle = setTimeout(() => {
            setDebouncedSearch(search.trim().toLowerCase());
        }, 300);
        return () => clearTimeout(handle);
    }, [search]);

    useEffect(() => () => {
        requestSeqRef.current += 1;
    }, []);

    const isProphuntSource = activeSource === 'prophunt_leads';
    const prophuntMeta = property?.prophunt;
    const prophuntStatus = String(prophuntMeta?.providerStatus || prophuntMeta?.status || '').toLowerCase();
    const hasInactiveStatus = [
        'delete',
        'removed',
        'inactive',
        'archive',
        'expired',
        'closed',
        'not_found',
        'failed',
        'error',
        'rejected',
    ].some((keyword) => prophuntStatus.includes(keyword));
    const hasProphuntAd = Boolean(
        (typeof prophuntMeta?.isActive === 'boolean' && prophuntMeta.isActive)
        || (!hasInactiveStatus && (prophuntMeta?.adId || prophuntMeta?.adUrl || prophuntMeta?.externalRefId))
    );

    useEffect(() => {
        if (isProphuntSource && sort === 'needs_followup') {
            setSort('best');
        }
    }, [isProphuntSource, sort]);

    const fetchMatches = async ({ offset = 0, append = false, force = false } = {}) => {
        if (!propertyId) return;
        const requestId = ++requestSeqRef.current;
        try {
            if (offset === 0) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }
            setError(null);
            const data = isProphuntSource
                ? await fetchPropHuntLeadsForProperty(propertyId, { q: debouncedSearch, sort })
                : (
                    force
                        ? await refreshMatchesForProperty(propertyId)
                        : await fetchMatchesForProperty(propertyId, { limit: 20, offset, sort, q: debouncedSearch })
                );
            if (requestId !== requestSeqRef.current) return;
            const results = data.results || data.items || data.matches || [];
            setMatchesData(data);
            setTotal(data.totalCandidates ?? results.length);
            setMatches((prev) => (append && !isProphuntSource ? [...prev, ...results] : results));
        } catch (err) {
            console.error('Failed to fetch property matches', {
                propertyId,
                offset,
                force,
                source: activeSource,
                error: err,
            });
            setError(err.message || 'Failed to fetch matches');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        if (!propertyId) return;
        setMatches([]);
        setTotal(0);
        fetchMatches({ offset: 0, append: false });
    }, [propertyId, sort, debouncedSearch, activeSource]);

    const filteredMatches = useMemo(() => {
        const q = debouncedSearch;
        let items = matches;
        if (q) {
            items = items.filter((match) => {
                const lead = match.leadSnapshot || {};
                return (
                    (lead.name || '').toLowerCase().includes(q)
                    || (lead.phone || '').toLowerCase().includes(q)
                    || (lead.email || '').toLowerCase().includes(q)
                );
            });
        }

        if (sort === 'recent') {
            return [...items].sort((a, b) => {
                const aDate = new Date(a.leadSnapshot?.createdAt || a.prophuntLead?.createdAt || 0).getTime();
                const bDate = new Date(b.leadSnapshot?.createdAt || b.prophuntLead?.createdAt || 0).getTime();
                return bDate - aDate;
            });
        }
        if (sort === 'needs_followup' && !isProphuntSource) {
            return [...items].sort((a, b) => {
                const aOverdue = a.activitySummary?.overdueCount || 0;
                const bOverdue = b.activitySummary?.overdueCount || 0;
                if (bOverdue !== aOverdue) return bOverdue - aOverdue;
                const aNext = a.activitySummary?.nextFollowUpAt || Infinity;
                const bNext = b.activitySummary?.nextFollowUpAt || Infinity;
                return aNext - bNext;
            });
        }
        return [...items].sort((a, b) => (b.score || 0) - (a.score || 0));
    }, [matches, debouncedSearch, sort, isProphuntSource]);

    if (!property) return null;

    const handleOpenLead = (leadId) => {
        history.push(`${basePath}/leads/${leadId}`);
    };

    const handleWhatsApp = (lead) => {
        if (!lead?.phone) return;
        const text = `Hi ${lead.name || 'there'}, I have a property that matches your requirements: ${property.title || 'Property'} in ${property.locality || property.city || ''}.`;
        window.open(`https://wa.me/${lead.phone.replace(/\+/g, '')}?text=${encodeURIComponent(text)}`, '_blank');
    };

    const handleScheduleFollowUp = async (lead) => {
        try {
            const dueAt = defaultFollowupAt();
            await createActivity({
                entityType: 'lead',
                entityId: lead.id,
                type: 'task',
                title: `Follow-up for ${property.title || 'Property'}`,
                dueAt,
                meta: {
                    propertyId,
                    propertyTitle: property.title,
                },
                propertyId,
            });
            showSuccess('Follow-up scheduled');
            fetchMatches({ offset: 0, append: false, force: true });
        } catch (err) {
            showError(err.message || 'Failed to schedule follow-up');
        }
    };

    const handleCreateDeal = async (lead) => {
        try {
            if (lead.dealId) {
                showSuccess('Deal already exists');
                history.push(`${basePath}/deals/${lead.dealId}`);
                return;
            }
            const result = await createDealFromMatch(lead.id, {
                propertyId,
                propertyTitle: property.title,
                value: property.price || 0,
            });
            if (result?.deal?.id) {
                showSuccess('Deal created');
                history.push(`${basePath}/deals/${result.deal.id}`);
            } else {
                showSuccess('Deal created');
            }
        } catch (err) {
            showError(err.message || 'Failed to create deal');
        }
    };

    const handleImportPropHuntLead = async (match) => {
        const lead = match?.leadSnapshot || {};
        const fullName = String(lead.name || '').trim() || 'PropHunt Lead';
        const [firstName, ...rest] = fullName.split(/\s+/);
        const lastName = rest.join(' ').trim();
        const phone = String(lead.phone || '').trim();
        const email = String(lead.email || '').trim();

        if (!phone && !email) {
            showError('Lead has no phone or email to import');
            return;
        }

        try {
            const created = await createLead({
                firstName: firstName || fullName,
                lastName,
                phone: phone || undefined,
                email: email || undefined,
                source: 'other',
                status: 'new',
                leadStage: 'new',
                tags: ['prophunt'],
                notes: `Imported from PropHunt for property ${property?.title || propertyId}.`,
            });
            showSuccess('Lead imported');
            if (created?.id) {
                history.push(`${basePath}/leads/${created.id}`);
            }
        } catch (err) {
            showError(err.message || 'Failed to import lead');
        }
    };

    const handleMakePublic = () => {
        if (typeof onMakePublic === 'function') {
            onMakePublic();
            return;
        }
        onClose?.();
    };

    const locationLabel = [property.locality, property.city].filter(Boolean).join(', ');
    const subtitle = `${locationLabel || ''}${property.pincode ? ` • ${property.pincode}` : ''}`;

    return (
        <div className="crm-modal-overlay" onClick={onClose}>
            <div className="crm-modal crm-modal-lg" onClick={(e) => e.stopPropagation()}>
                <div className="crm-modal-header">
                    <div>
                        <h2>Matching Leads</h2>
                        <div className="crm-help-text">
                            {property.title || 'Property'} {subtitle ? `— ${subtitle}` : ''}{property.propertyType ? ` • ${property.propertyType}` : ''}
                        </div>
                    </div>
                    <button className="crm-modal-close" onClick={onClose}>&times;</button>
                </div>
                <div className="crm-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', background: 'var(--crm-gray-50)' }}>
                    <div className="crm-tabs" style={{ marginBottom: '12px' }}>
                        <button
                            className={`crm-tab ${activeSource === 'my_leads' ? 'active' : ''}`}
                            onClick={() => setActiveSource('my_leads')}
                            type="button"
                        >
                            My Leads
                        </button>
                        <button
                            className={`crm-tab ${activeSource === 'prophunt_leads' ? 'active' : ''}`}
                            onClick={() => setActiveSource('prophunt_leads')}
                            type="button"
                        >
                            Prophunt Leads
                        </button>
                    </div>

                    <div className="crm-match-controls">
                        <input
                            className="crm-input"
                            placeholder="Search leads..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <select className="crm-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                            <option value="best">Best match</option>
                            <option value="recent">Most recent</option>
                            {!isProphuntSource && <option value="needs_followup">Needs follow-up</option>}
                        </select>
                        <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => fetchMatches({ offset: 0, append: false, force: true })}>
                            Refresh
                        </button>
                    </div>

                    {loading && (
                        <div className="crm-loading">
                            {isProphuntSource ? 'Fetching PropHunt leads...' : 'Finding matches...'}
                        </div>
                    )}
                    {error && (
                        <div className="crm-empty-state">
                            <h3>{isProphuntSource ? 'Failed to load PropHunt leads' : 'Failed to load matches'}</h3>
                            <p>{error}</p>
                            <button
                                className="crm-btn crm-btn-primary crm-btn-sm"
                                onClick={() => fetchMatches({ offset: 0, append: false, force: true })}
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {!loading && !error && (
                        <>
                            <div className="crm-help-text" style={{ marginBottom: '12px' }}>
                                Found {total} {isProphuntSource ? 'PropHunt lead' : 'lead'}{total === 1 ? '' : 's'}
                            </div>

                            {filteredMatches.length > 0 ? (
                                <div className="crm-match-list">
                                    {filteredMatches.map((match) => {
                                        const lead = match.leadSnapshot || {};
                                        const summary = match.activitySummary || {};
                                        const isProphuntLead = Boolean(match.prophuntLead);
                                        const contacted = summary.contacted;
                                        const score = Math.round(match.score || 0);
                                        const scoreStyle = score >= 80 ? 'high' : score >= 50 ? 'mid' : 'low';
                                        const reasons = (match.reasons || []).map((reason) => reason.label).filter(Boolean);
                                        const lastActivityText = summary.lastActivityLabel
                                            ? `Last: ${summary.lastActivityLabel}${summary.lastActivityAt ? ` • ${formatRelativeTime(summary.lastActivityAt)}` : ''}`
                                            : 'No Activity';
                                        const matchIntent = match.breakdown?.intentValue?.split(':')[0];
                                        const propertyIntent = match.breakdown?.intentValue?.split(':')[1];
                                        const intentStatus = match.breakdown?.intentStatus || 'unknown';
                                        const intentLabel = matchIntent === 'rent' ? 'Wants Rent' : matchIntent === 'sale' ? 'Wants Buy' : 'Intent Unknown';
                                        const statusLabel = intentStatus === 'match' ? 'Intent Match' : intentStatus === 'mismatch' ? 'Intent Mismatch' : 'Intent Unknown';
                                        const leadRecordId = lead.id || null;

                                        return (
                                            <div key={match.leadId} className="crm-card crm-match-row">
                                                <div className="crm-card-body">
                                                    <div className="crm-match-row-top">
                                                        <div className="crm-match-score">
                                                            <div className={`crm-match-score-value ${scoreStyle}`}>{score}</div>
                                                            <div className="crm-match-score-label">Match</div>
                                                        </div>
                                                        <div className="crm-match-row-body">
                                                            <div className="crm-match-row-title">{lead.name || 'Unnamed Lead'}</div>
                                                            <div className="crm-match-row-sub">
                                                                {lead.phone || 'No phone'} {lead.email ? `• ${lead.email}` : ''}
                                                            </div>
                                                            <div className="crm-match-row-meta">
                                                                <span className="crm-badge crm-badge-source">{lead.status || 'new'}</span>
                                                                {contacted && <span className="crm-badge crm-badge-available">Contacted</span>}
                                                                {isProphuntLead ? (
                                                                    <span className="crm-badge crm-badge-source">PropHunt</span>
                                                                ) : (
                                                                    <>
                                                                        <span className="crm-badge crm-badge-source">{intentLabel}</span>
                                                                        <span className={`crm-badge ${intentStatus === 'mismatch' ? 'crm-badge-danger' : 'crm-badge-source'}`}>{statusLabel}</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                            <div className="crm-match-row-activity">
                                                                {lastActivityText}
                                                                {summary.nextFollowUpAt && (
                                                                    <span> • Next follow-up: {new Date(summary.nextFollowUpAt).toLocaleString()}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="crm-match-row-actions">
                                                            <button
                                                                className="crm-btn crm-btn-secondary crm-btn-xs"
                                                                disabled={!lead.phone}
                                                                title={!lead.phone ? 'No phone' : 'WhatsApp'}
                                                                onClick={() => handleWhatsApp(lead)}
                                                            >
                                                                WhatsApp
                                                            </button>
                                                            <a
                                                                className={`crm-btn crm-btn-secondary crm-btn-xs ${!lead.phone ? 'crm-btn-disabled' : ''}`}
                                                                href={lead.phone ? `tel:${lead.phone}` : undefined}
                                                                onClick={(e) => !lead.phone && e.preventDefault()}
                                                                title={!lead.phone ? 'No phone' : 'Call'}
                                                            >
                                                                Call
                                                            </a>
                                                            <a
                                                                className={`crm-btn crm-btn-secondary crm-btn-xs ${!lead.email ? 'crm-btn-disabled' : ''}`}
                                                                href={lead.email ? `mailto:${lead.email}` : undefined}
                                                                onClick={(e) => !lead.email && e.preventDefault()}
                                                                title={!lead.email ? 'No email' : 'Email'}
                                                            >
                                                                Email
                                                            </a>
                                                            {isProphuntLead ? (
                                                                <button
                                                                    className="crm-btn crm-btn-primary crm-btn-xs"
                                                                    onClick={() => handleImportPropHuntLead(match)}
                                                                >
                                                                    Import Lead
                                                                </button>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        className="crm-btn crm-btn-primary crm-btn-xs"
                                                                        onClick={() => handleScheduleFollowUp(lead)}
                                                                    >
                                                                        Schedule Follow-up
                                                                    </button>
                                                                    <button
                                                                        className="crm-btn crm-btn-secondary crm-btn-xs"
                                                                        disabled={!leadRecordId}
                                                                        onClick={() => leadRecordId && handleOpenLead(leadRecordId)}
                                                                    >
                                                                        Open Lead
                                                                    </button>
                                                                    <button
                                                                        className="crm-btn crm-btn-primary crm-btn-xs"
                                                                        onClick={() => handleCreateDeal(lead)}
                                                                    >
                                                                        Create Deal
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="crm-match-bar">
                                                        <span style={{ width: `${Math.min(score, 100)}%` }} />
                                                    </div>
                                                    <div className="crm-match-reasons">
                                                        {reasons.length > 0 ? reasons.map((reason) => (
                                                            <span key={reason} className="crm-reason-chip">{reason}</span>
                                                        )) : <span className="crm-reason-chip">No strong signals</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="crm-empty-state">
                                    {isProphuntSource ? (
                                        hasProphuntAd ? (
                                            <>
                                                <h3>No PropHunt leads found for this listing</h3>
                                                <p>Leads will appear here after PropHunt receives enquiries for this ad.</p>
                                            </>
                                        ) : (
                                            <>
                                                <h3>Make Public</h3>
                                                <p>Post on Prophunt to receive leads from Prophunt.</p>
                                                <button className="crm-btn crm-btn-primary crm-btn-sm" onClick={handleMakePublic}>
                                                    Make Public
                                                </button>
                                            </>
                                        )
                                    ) : (
                                        <>
                                            <h3>No leads match this property type yet</h3>
                                            <p>Create leads or import to see matches.</p>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button className="crm-btn crm-btn-primary" onClick={() => history.push(`${basePath}/leads/new`)}>
                                                    Create lead
                                                </button>
                                                <button className="crm-btn crm-btn-secondary" onClick={() => history.push(`${basePath}/leads?import=true`)}>
                                                    Import leads
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {!isProphuntSource && matches.length < total && (
                                <div className="crm-match-loadmore">
                                    <button
                                        className="crm-btn crm-btn-secondary crm-btn-sm"
                                        onClick={() => fetchMatches({ offset: matches.length, append: true })}
                                        disabled={loadingMore}
                                    >
                                        {loadingMore ? 'Loading...' : 'Load more'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PropertyMatchesModal;
